import { useState } from 'react';
import { X, RefreshCw, HeartHandshake, CreditCard } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

interface PaymentsHistoryModalProps {
    show: boolean;
    onClose: () => void;
    payments: any[];
    loading: boolean;
    checkingId: number | null;
    onCheckStatus: (id: number) => void;
    donations: any[];
    donationsLoading: boolean;
}

export function PaymentsHistoryModal({
    show,
    onClose,
    payments,
    loading,
    checkingId,
    onCheckStatus,
    donations,
    donationsLoading,
}: PaymentsHistoryModalProps) {
    const [tab, setTab] = useState<'pagamentos' | 'doacoes'>('pagamentos');

    if (!show) return null;

    const tabBtn = (active: boolean) =>
        `flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-bold transition-colors rounded-lg ${
            active
                ? 'bg-white/10 text-white'
                : 'text-white/40 hover:text-white/70'
        }`;

    return (
        <>
            <div
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-[fadeInModal_0.2s_ease-out]"
                onClick={onClose}
            />

            <div className="fixed bottom-0 left-0 right-0 z-50 animate-[slideUp_0.3s_ease-out] safe-area-inset-bottom">
                <div className="bg-neutral-950 w-full rounded-t-2xl flex flex-col max-h-[85vh]">
                    <div className="flex justify-center pt-3 pb-1">
                        <div className="w-12 h-1 bg-white/10 rounded-full" />
                    </div>

                    <div className="px-6 pb-3 flex justify-between items-center flex-shrink-0">
                        <h3 className="text-lg font-bold text-white">Minhas doações</h3>
                        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="px-6 pb-3 flex-shrink-0">
                        <div className="flex gap-1 bg-white/[0.06] p-1 rounded-xl">
                            <button className={tabBtn(tab === 'pagamentos')} onClick={() => setTab('pagamentos')}>
                                <CreditCard size={15} strokeWidth={2} />
                                Pagamentos
                            </button>
                            <button className={tabBtn(tab === 'doacoes')} onClick={() => setTab('doacoes')}>
                                <HeartHandshake size={15} strokeWidth={2} />
                                Doações
                            </button>
                        </div>
                    </div>

                    <div className="px-6 overflow-y-auto flex-1 pb-32 md:pb-8">
                        {tab === 'pagamentos' ? (
                            loading ? (
                                <div className="flex justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
                                </div>
                            ) : payments.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    {payments.map((payment) => (
                                        <div
                                            key={payment.id}
                                            className="bg-white/5 rounded-xl p-4 border border-white/5 flex justify-between items-center"
                                        >
                                            <div>
                                                <div className="font-bold text-white text-base mb-1">
                                                    {payment.plan?.name || 'Plano VIP'}
                                                </div>
                                                <div className="text-white/40 text-xs">
                                                    {dayjs(payment.createdAt).format('DD/MM/YYYY [às] HH:mm')}
                                                </div>
                                            </div>
                                            <div className="text-right flex flex-col items-end gap-2">
                                                <span
                                                    className={`text-xs font-bold px-2 py-1 rounded capitalize ${
                                                        payment.status === 'completed'
                                                            ? 'bg-green-500/20 text-green-400'
                                                            : payment.status === 'pending'
                                                            ? 'bg-yellow-500/20 text-yellow-400'
                                                            : 'bg-red-500/20 text-red-400'
                                                    }`}
                                                >
                                                    {payment.status === 'completed'
                                                        ? 'Aprovado'
                                                        : payment.status === 'pending'
                                                        ? 'Pendente'
                                                        : 'Rejeitado'}
                                                </span>
                                                {payment.status === 'pending' && (
                                                    <button
                                                        onClick={() => onCheckStatus(payment.id)}
                                                        disabled={checkingId === payment.id}
                                                        className="flex items-center gap-1.5 text-[10px] font-bold bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-white transition-colors disabled:opacity-50"
                                                    >
                                                        <RefreshCw
                                                            size={10}
                                                            className={checkingId === payment.id ? 'animate-spin' : ''}
                                                        />
                                                        Verificar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-white/40">Nenhum pagamento encontrado.</div>
                            )
                        ) : donationsLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
                            </div>
                        ) : donations.length > 0 ? (
                            <div className="flex flex-col gap-3">
                                {donations.map((d, i) => (
                                    <div
                                        key={d.odi_id ?? i}
                                        className="bg-white/5 rounded-xl p-4 border border-white/5 flex justify-between items-center"
                                    >
                                        <div>
                                            <div className="font-bold text-white text-base mb-1">
                                                {d.obr_nome ?? 'Obra'}
                                            </div>
                                            <div className="text-white/40 text-xs">
                                                {dayjs(d.paid_at ?? d.created_at).format('DD/MM/YYYY [às] HH:mm')}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-orange-400 text-base">
                                                R$ {Number(d.amount ?? 0).toFixed(2).replace('.', ',')}
                                            </div>
                                            <div
                                                className={`text-xs font-bold mt-1 ${
                                                    d.status === 'completed'
                                                        ? 'text-green-400'
                                                        : d.status === 'pending'
                                                        ? 'text-yellow-400'
                                                        : 'text-white/40'
                                                }`}
                                            >
                                                {d.status === 'completed'
                                                    ? 'Concluída'
                                                    : d.status === 'pending'
                                                    ? 'Pendente'
                                                    : d.status ?? ''}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-white/40">Nenhuma doação encontrada.</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
